import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { useAuth } from "@/hooks/useAuth";
import ProductCard from "./components/ProductCard";
import FilterBar from "./components/FilterBar";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") || "";

  // 兼容舊版 filter 參數，自動映射為 region
  const rawFilter = searchParams.get("filter");
  const mappedRegion = rawFilter === "hot"
    ? "體驗認購區"
    : rawFilter === "new"
    ? "VIP認購區"
    : "";

  const initialRegion = searchParams.get("region") || mappedRegion || "全部商品";
  const initialCategory = searchParams.get("category") || "";

  const [searchValue, setSearchValue] = useState(initialSearch);
  const [activeRegion, setActiveRegion] = useState(initialRegion);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const { user } = useAuth();
  const { products, isLoading } = useProducts();
  const [sortBy, setSortBy] = useState<"default" | "price-asc" | "price-desc" | "profit">("default");

  // 根據當前選中的大區，動態提取小分類（不包含大區名稱）
  const regionCategories = useMemo(() => {
    if (activeRegion === "吉時好物") {
      return ["奶粉罐回收兌換用品區"];
    }
    const cats = new Set<string>();
    for (const p of products) {
      if (p.region === activeRegion && p.category?.trim()) {
        cats.add(p.category.trim());
      }
    }
    return Array.from(cats).sort();
  }, [products, activeRegion]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (searchValue) newParams.set("search", searchValue);
    else newParams.delete("search");
    if (activeRegion && activeRegion !== "全部商品") newParams.set("region", activeRegion);
    else newParams.delete("region");
    if (activeCategory) newParams.set("category", activeCategory);
    else newParams.delete("category");
    setSearchParams(newParams, { replace: true });
  }, [activeRegion, activeCategory, sortBy, setSearchParams, searchParams, searchValue]);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (searchValue.trim()) {
      const q = searchValue.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.region.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    // 大區篩選
    if (activeRegion && activeRegion !== "全部商品") {
      result = result.filter((p) => p.region === activeRegion);
    }

    // 小分類篩選
    if (activeCategory) {
      result = result.filter((p) => p.category === activeCategory);
    }

    if (sortBy === "price-asc") {
      result.sort((a, b) => a.distributionPrice - b.distributionPrice);
    } else if (sortBy === "price-desc") {
      result.sort((a, b) => b.distributionPrice - a.distributionPrice);
    } else if (sortBy === "profit") {
      result.sort(
        (a, b) =>
          (b.suggestedPrice - b.distributionPrice) -
          (a.suggestedPrice - a.distributionPrice)
      );
    }

    return result;
  }, [searchValue, activeRegion, activeCategory, sortBy, products]);

  const handleSearch = (val: string) => {
    setSearchValue(val);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <div className="pt-20 pb-6 bg-stone-50">
        <div className="px-6 lg:px-12">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">全部貨源</h1>
          <p className="text-gray-500 text-sm">
            精選 {products.length}+ 款跨境優質貨源，直連工廠，超低認購價
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-12 py-6">
        <FilterBar
          searchValue={searchValue}
          onSearch={handleSearch}
          activeRegion={activeRegion}
          onRegionChange={setActiveRegion}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory}
          sortBy={sortBy}
          onSortChange={setSortBy}
          categories={regionCategories}
          totalCount={filteredProducts.length}
        />

        {/* 吉時好物區尚未推出提示 */}
        {activeRegion === "吉時好物" && (
          <div className="mt-10 flex flex-col items-center justify-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-50 flex items-center justify-center">
              <i className="ri-gift-2-line text-4xl text-amber-400"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">吉時好物區</h2>
            <div className="px-6 py-4 rounded-2xl bg-amber-50 border border-amber-200 text-center max-w-md">
              <p className="text-amber-700 font-semibold text-lg mb-1">此功能尚未推出，敬請期待</p>
              <p className="text-amber-600 text-sm">吉時好物區目前正在籍备中，我們正將精心筛選優質喗品，敬請期待首批商品上架！</p>
            </div>
            <p className="text-gray-400 text-sm mt-6">歓迎先擅總其他區區域的優質貨源</p>
          </div>
        )}

        {/* Results */}
        <div className="mt-6">
          {activeRegion === "吉時好物" ? null : isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                    <div className="h-5 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-4xl text-gray-300"></i>
              </div>
              <p className="text-gray-500 text-lg mb-2">未找到符合條件的商品</p>
              <p className="text-gray-400 text-sm">試試其他搜索關鍵詞或篩選條件</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
